import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Inline, Text, Anchor, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AtlassianIcon } from '@atlaskit/logo';
import WelcomeFeedIcon from '@atlaskit/icon-lab/core/welcome-feed';
import SunsetIcon from '@atlaskit/icon-lab/core/sunset';

import { FeedType } from '../useFeedTypeSelector.ts';

export const i18n = defineMessages({
	popularEndBannerText: {
		id: 'feed.end-of-feed-home-banner.popular-banner',
		defaultMessage: 'Discover what else is popular across your organisation',
		description: 'End of feed home banner text for popular content',
	},
	popularEndBannerLinkText: {
		id: 'feed.end-of-feed-home-banner.popular-link',
		defaultMessage: 'View all popular content',
		description: 'End of feed home banner link text for popular content',
	},
	welcomeEndBannerText: {
		id: 'feed.end-of-feed-home-banner.welcome-banner',
		defaultMessage: 'See who else is new at Atlassian',
		description: 'End of feed home banner text for welcome content',
	},
	welcomeEndBannerLinkText: {
		id: 'feed.end-of-feed-home-banner.welcome-link',
		defaultMessage: 'View all welcomes',
		description: 'End of feed home banner link text for welcome content',
	},
	farewellEndBannerText: {
		id: 'feed.end-of-feed-home-banner.farewell-banner',
		defaultMessage: 'See who else is leaving Atlassian',
		description: 'End of feed home banner text for farewell content',
	},
	farewellEndBannerLinkText: {
		id: 'feed.end-of-feed-home-banner.farewell-link',
		defaultMessage: 'View all farewells',
		description: 'End of feed home banner link text for farewell content',
	},
	welcomeIconLabel: {
		id: 'feed.end-of-feed-home-banner.welcome-icon',
		defaultMessage: 'Welcome',
		description: 'End of feed home banner icon label for welcome content',
	},
	farewellIconLabel: {
		id: 'feed.end-of-feed-home-banner.farewell-icon',
		defaultMessage: 'Farewell',
		description: 'End of feed home banner icon label for farewell content',
	},
});

const linkTextStyles = xcss({
	marginLeft: 'space.100',
	marginRight: 'space.100',
});

const anchorStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	textDecoration: 'none',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	display: 'inline-flex',
	alignItems: 'center',
	gap: 'space.100',
	paddingInline: 'space.050',
	paddingBlock: 'space.025',
	marginRight: 'space.100',
	paddingRight: 'space.100',

	':hover': {
		textDecoration: 'none',
	},
	':active': {
		color: 'color.link.pressed',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
});

const bannerContainerStyles = xcss({
	backgroundColor: 'color.background.input.hovered',
	borderRadius: 'radius.small',
	marginTop: 'space.100',
	padding: 'space.100',
	height: '64px',
});

const bannerTextStyles = xcss({
	marginLeft: 'space.100',
});

const singleIconStyles = xcss({
	marginRight: 'space.200',
});

const homeIconStyles = xcss({
	height: '32px',
});

export type EndOfFeedHomeBannerProps = {
	linkHref: string;
	feedType: FeedType;
};

const getBannerText = (feedType: FeedType) => {
	switch (feedType) {
		case FeedType.WELCOME:
			return [i18n.welcomeEndBannerText, i18n.welcomeEndBannerLinkText];
		case FeedType.FAREWELL:
			return [i18n.farewellEndBannerText, i18n.farewellEndBannerLinkText];
		case FeedType.POPULAR:
			return [i18n.popularEndBannerText, i18n.popularEndBannerLinkText];
		default:
			return [];
	}
};

export const EndOfFeedHomeBanner: FC<EndOfFeedHomeBannerProps> = ({ linkHref, feedType }) => {
	const [text, linkText] = getBannerText(feedType);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getLinkToRender = () => {
		switch (feedType) {
			case FeedType.WELCOME:
				return `${linkHref}&tab=welcome`;
			case FeedType.FAREWELL:
				return `${linkHref}&tab=farewell`;
			case FeedType.POPULAR:
			default:
				return linkHref;
		}
	};

	const renderIcons = () => {
		switch (feedType) {
			case FeedType.POPULAR:
				return (
					<Box testId="icon-group" xcss={[singleIconStyles, homeIconStyles]}>
						<AtlassianIcon />
					</Box>
				);
			case FeedType.WELCOME:
				return (
					<Flex testId="welcome-icon" xcss={singleIconStyles}>
						<WelcomeFeedIcon label={i18n.welcomeIconLabel.defaultMessage} />
					</Flex>
				);
			case FeedType.FAREWELL:
				return (
					<Flex testId="farewell-icon" xcss={singleIconStyles}>
						<SunsetIcon label={i18n.farewellIconLabel.defaultMessage} />
					</Flex>
				);
		}
	};

	const handleLinkClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'reducedFeed',
				action: 'clicked',
				actionSubject: 'link',
				attributes: {
					feedType,
				},
			},
		}).fire();
	};

	return (
		<Flex
			testId="end-of-feed-home-banner"
			alignItems="center"
			justifyContent="space-between"
			xcss={bannerContainerStyles}
		>
			<Box xcss={bannerTextStyles}>
				<Inline alignBlock="center">
					{renderIcons()}
					<Text>
						<FormattedMessage {...text} />
					</Text>
				</Inline>
			</Box>
			<Box>
				<Anchor
					xcss={anchorStyles}
					href={getLinkToRender()}
					onClick={handleLinkClick}
					target="_blank"
					rel="noopener noreferrer"
				>
					<Inline alignBlock="center">
						<Box xcss={linkTextStyles}>
							<Text color="color.text.subtle">
								<FormattedMessage {...linkText} />
							</Text>
						</Box>
						<LinkExternalIcon color={token('color.icon.subtle')} label="Open" size="small" />
					</Inline>
				</Anchor>
			</Box>
		</Flex>
	);
};
